import { useCallback, useMemo } from 'react';
import { TypedDocumentNode } from '@datocms/cda-client';
import useAsyncMemo from 'hooks/useAsyncMemo';
import { useRouter } from 'next/router';
import useConfig from 'services/config';
import { query, QueryOptions } from 'services/graphql/client';
import { isDev } from 'utils/dom';

export function usePaginatedQuery<
  Result = unknown,
  Variables extends { first: number; skip: number } = { first: number; skip: number },
>(
  gqlQuery: TypedDocumentNode<Result, Variables> | string,
  variables?: Omit<Variables, 'first' | 'skip'>,
  options?: QueryOptions<Variables> & { skip?: boolean; itemsPerPage?: number; resultKey: keyof Result },
) {
  const { tenant } = useConfig();
  const itemsPerPage = options?.itemsPerPage || 2;
  const resultKey = options?.resultKey;
  const router = useRouter();
  const page = useMemo(() => parseInt(router.query.page?.toString() || '1'), [router.query]);

  const setCurrentPage = useCallback(
    (page: number) => {
      router.push({ query: { ...router.query, tenant, page } }, undefined, { shallow: true });
    },
    [router, tenant],
  );

  const result = useAsyncMemo(async () => {
    if (options?.skip) return undefined;

    const response = await query(
      gqlQuery,
      { ...variables, first: itemsPerPage, skip: (page - 1) * itemsPerPage } as Variables,
      options,
    );
    return response;
  }, [gqlQuery, JSON.stringify(variables), page, JSON.stringify(options)]);

  return useMemo(() => {
    const defaultResult = {
      items: undefined,
      currentPage: page,
      numberOfPages: 1,
      loading: true,
      goToPage: (_page: number) => {
        /* */
      },
    };

    if (!resultKey) {
      if (isDev) console.warn(' ✗ Paginated query has no specified result key');
      return defaultResult;
    }

    if (!result) {
      return defaultResult;
    }

    const allItems = (result as any)[`_${resultKey as string}Meta`]?.count;
    if (typeof allItems !== 'number') {
      if (isDev)
        console.warn(
          ' ✗ Paginated query is not returning meta with count. Read more here: %s',
          'https://tinyurl.com/dato-count',
        );
      return defaultResult;
    }

    const numberOfPages = Math.ceil(allItems / itemsPerPage);
    const items = result[resultKey];
    const goToPage = (page: number) => page <= numberOfPages && page > 0 && setCurrentPage(page);

    return {
      numberOfPages,
      currentPage: page,
      items,
      loading: false,
      goToPage,
    };
  }, [result, resultKey, page, itemsPerPage, setCurrentPage]);
}
